import { CheckoutStep } from '~/types/checkout'
import { UserRole } from '~/types/account'

export type UserType = 'guest' | 'customer' | 'demonstrator'
export type LoginStatus = 'authenticated' | 'unauthenticated'
export type SiteLocation = 'customer_site' | 'demonstrator_portal'
export type ViewMode = 'standard' | 'customer_view'
export type OrderType = 'customer_order' | 'demonstrator_order' | 'gift_order' | 'none'
export type CartType = 'standard' | 'shared' | 'subscription' | 'demonstrator' | 'customer' | 'none'
export type TrackingSubscriptionType = 'monthly' | 'monthly_gift' | 'prepay' | 'prepay_gift'
export type PageSection =
  | 'shipping_address' | 'payment_method' | 'shipping_method' | 'demonstrator_selection'
  | 'gift_recipient' | 'gift_notification' | 'agreement' | 'review_submit'
  | 'customer_selection' | 'product_selection'
export type CheckboxId =
  | 'gift_recipient_acknowledgment' | 'recurring_charges_agreement' | 'terms_and_conditions'
  | 'shipping_address_acknowledgment' | 'demonstrator_directory_agreement'

/** CheckoutStep enum -> schema page_section. GIFT_MESSAGE carries the gift_notification step. */
export const CHECKOUT_STEP_TO_PAGE_SECTION: Record<CheckoutStep, PageSection> = {
  [CheckoutStep.ADDRESS]: 'shipping_address',
  [CheckoutStep.PAYMENT]: 'payment_method',
  [CheckoutStep.SHIPPING]: 'shipping_method',
  [CheckoutStep.DEMO]: 'demonstrator_selection',
  [CheckoutStep.GIFT_MESSAGE]: 'gift_notification',
  [CheckoutStep.AGREEMENT]: 'agreement',
  [CheckoutStep.COMPLETE]: 'review_submit'
}

/**
 * Recruit maps to 'customer' rather than falling through to 'guest'.
 *
 * verifyCustomer() in the join store sets UserRole.Recruit, and loginStatusFromRole already
 * returns 'authenticated' for it — so without this, every event after a join sign-in carried
 * the contradictory pair `user_type: guest` + `login_status: authenticated`. Both routes into
 * the join flow (an existing customer signing in, and a new account registration) leave an
 * account holder behind, so 'customer' is the honest reading.
 *
 * PENDING the analyst's confirmation: 'customer' is our reading, not a value taken from the
 * schema. If recruits should be separated in reporting this becomes a new UserType member
 * instead — a one-line change here.
 */
export function userTypeFromRole (role?: UserRole): UserType {
  if (role === UserRole.Customer || role === UserRole.Recruit) { return 'customer' }
  if (role === UserRole.Demonstrator || role === UserRole.Agent) { return 'demonstrator' }
  return 'guest'
}

/**
 * Maps a UserRole to GA4 login_status.
 *
 * Demonstrator and Agent intentionally resolve to 'authenticated': in GA4 checkout
 * flows a demonstrator or agent placing a proxy order is a logged-in human (per the
 * schema's Flow Reference). This is distinct from the auth plugin's customer-account
 * `isAuthenticated` gating, which does not apply here.
 */
export function loginStatusFromRole (role?: UserRole): LoginStatus {
  return (role === undefined || role === UserRole.Guest || role === UserRole.Anonymous)
    ? 'unauthenticated'
    : 'authenticated'
}

export function siteLocationFromPath (path: string): SiteLocation {
  return path.includes('/customer-ordering/') ? 'demonstrator_portal' : 'customer_site'
}

export function viewMode (userType: UserType, siteLocation: SiteLocation): ViewMode {
  return userType === 'demonstrator' && siteLocation === 'customer_site' ? 'customer_view' : 'standard'
}

export interface CheckoutContext {
  role?: UserRole
  path: string
  orderType: OrderType
  cartType: CartType
  subscriptionType?: TrackingSubscriptionType
  isGift: boolean
}

/** A renderer supplies the flow-specific half of the context; identity/site come from auth + route. */
export type CheckoutContextInput = Omit<CheckoutContext, 'role' | 'path'>

export interface CheckoutTrackingParams {
  user_type: UserType
  login_status: LoginStatus
  order_type: OrderType
  cart_type: CartType
  subscription_type?: TrackingSubscriptionType
  site_location: SiteLocation
  view_mode: ViewMode
  is_gift: boolean
}

/** The flow signals a checkout renderer can observe for the order it is rendering. */
export interface CheckoutFlowSignals {
  isSubscription: boolean
  isGift: boolean
  isPrePaid: boolean
  isDemonstrator: boolean
  isSharedCart: boolean
}

/**
 * Derives order_type/cart_type/subscription_type from the flow signals.
 *
 * Two renderers report on one order — CheckoutBase for the step events and
 * CheckoutConfirmation for the purchase event — and a funnel splits on those params,
 * so the precedence lives here instead of being written out once per renderer.
 *
 * The renderers cannot share the *sources*: a shared cart clears its cookie in
 * exitSharedCart() before the confirmation route mounts, so CheckoutConfirmation
 * passes the mode captured at submit where CheckoutBase reads the live store.
 *
 * DELIBERATE DEVIATION from nuxt2 (flagged in review): nuxt2's
 * CheckoutConfirmation derived these independently and only ever resolved
 * 'subscription'|'standard' and 'customer_order'|'gift_order'. A demonstrator or
 * shared-cart order therefore reported demonstrator/shared on every
 * checkout_step_view and then fell back to standard/customer_order on the purchase
 * event for the same order, splitting the funnel on the one event it exists to
 * terminate. The plan requires purchase to carry the same 8 custom params
 * (nuxt2 checkout, docs/superpowers/plans/2026-06-04-ga4-checkout-funnel-tracking.md:
 * "spot-check that purchase now carries the 8 custom params alongside its
 * ecommerce payload"), so the narrowing is a nuxt2 bug this port does not
 * reproduce. Flow 9 (demo order) and flow 3 (customer shared cart) are the affected
 * cases, and the superset derivation is what nuxt2's own CheckoutBase already used
 * for the step events.
 *
 * The narrowing reached this repo with the purchase wiring itself: origin/master's
 * CheckoutConfirmation called pushCheckoutComplete with no tracking params at all,
 * so both the wiring and the shape it originally copied are this branch's to get
 * right.
 */
export function checkoutContextFromFlow (flow: CheckoutFlowSignals): CheckoutContextInput {
  let cartType: CartType = 'standard'
  if (flow.isSubscription) { cartType = 'subscription' } else if (flow.isSharedCart) { cartType = 'shared' } else if (flow.isDemonstrator) { cartType = 'demonstrator' }

  let orderType: OrderType = 'customer_order'
  if (flow.isGift) { orderType = 'gift_order' } else if (flow.isDemonstrator && !flow.isSubscription) { orderType = 'demonstrator_order' }

  let subscriptionType: TrackingSubscriptionType | undefined
  if (flow.isSubscription) {
    subscriptionType = flow.isPrePaid
      ? (flow.isGift ? 'prepay_gift' : 'prepay')
      : (flow.isGift ? 'monthly_gift' : 'monthly')
  }

  return { orderType, cartType, subscriptionType, isGift: flow.isGift }
}

export function buildCheckoutParams (ctx: CheckoutContext): CheckoutTrackingParams {
  const userType = userTypeFromRole(ctx.role)
  const siteLocation = siteLocationFromPath(ctx.path)
  const params: CheckoutTrackingParams = {
    user_type: userType,
    login_status: loginStatusFromRole(ctx.role),
    order_type: ctx.orderType,
    cart_type: ctx.cartType,
    site_location: siteLocation,
    view_mode: viewMode(userType, siteLocation),
    is_gift: ctx.isGift
  }
  if (ctx.subscriptionType) { params.subscription_type = ctx.subscriptionType }
  return params
}
